/**
 * Per-company settings override page (Phase 4 of settings-overhaul-plan.md).
 *
 * Super-admin only. Shows every setting key that has a platform default OR
 * an org override, with a three-state badge per row:
 *   - blue   "Custom override" + Edit + Reset
 *   - gray   "Platform default" + Override
 *   - red    "Not configured" (rare — only if both are empty after a delete race)
 *
 * Editing writes to the org scope; resetting deletes the org override and
 * the row falls back to the platform default.
 *
 * Rows are bucketed into the same intent groups as the tenant settings rail
 * (`-settings/key-groups.ts`) — Recording, AI & models, Reports, Spending,
 * Workspace, plus a "Platform & advanced" catch-all — with a search box across
 * key + label + group and a per-group "N keys · M overridden" summary. The raw
 * override semantics (three-state badge, masked secrets, Edit/Override + Reset)
 * are unchanged.
 */

import {
  isEnsembleHeadcountTie,
  isEnsembleMergeMode,
  isTranscriptionMode,
  type TEnsembleRole,
  TRANSCRIPTION_LABELS,
  TURNS_CAPABLE_PROVIDERS,
  validateEngineList,
  WORDS_CAPABLE_PROVIDERS,
} from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Modal,
  MultiSelect,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { IconEye, IconEyeOff, IconSearch } from "@tabler/icons-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"
import { TableSkeleton } from "../../../components/PageSkeletons"
import { deleteCompanySettingOverride, fetchCompanySettings, updateCompanySettings } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { notifyError } from "../../../lib/notify"
import { queryKeys } from "../../../lib/query-keys"
import { requireAdmin } from "../../../lib/route-guards"
import { SETTING_KEY_LABELS } from "../-settings/constants"
import { ADMIN_SETTINGS_GROUP_META } from "../-settings/groups"
import { groupForSettingKey, type TSettingsKeyGroup } from "../-settings/key-groups"

function isSecretKey(key: string): boolean {
  return /(_key|_secret|_password)$/i.test(key)
}

/**
 * Human-readable label for a raw snake_case setting key (e.g.
 * "platform_anthropic_key" → "Anthropic (Claude) API key"). Falls back to the
 * raw key when no label is catalogued, so the table never shows a blank cell.
 */
function labelForKey(key: string): string {
  return SETTING_KEY_LABELS[key] ?? key
}

type TRowState = "override" | "default" | "unset"

interface IRow {
  key: string
  label: string
  group: TSettingsKeyGroup
  state: TRowState
  effectiveValue: string
  platformValue: string | undefined
  orgValue: string | undefined
}

interface IGroupSection {
  group: TSettingsKeyGroup
  rows: IRow[]
  overridden: number
}

function buildRows(platform: Record<string, string>, org: Record<string, string>): IRow[] {
  const allKeys = new Set<string>([...Object.keys(platform), ...Object.keys(org)])
  const rows: IRow[] = []
  for (const key of allKeys) {
    const orgValue = org[key]
    const platformValue = platform[key]
    let state: TRowState
    if (orgValue !== undefined && orgValue !== "") state = "override"
    else if (platformValue !== undefined && platformValue !== "") state = "default"
    else state = "unset"
    rows.push({
      key,
      label: labelForKey(key),
      group: groupForSettingKey(key),
      state,
      effectiveValue: orgValue !== undefined && orgValue !== "" ? orgValue : (platformValue ?? ""),
      platformValue,
      orgValue,
    })
  }
  return rows
}

/**
 * Bucket rows into their intent groups (in the fixed rail order), sorting each
 * group with overrides first and then alphabetically by human label. Empty groups
 * are dropped so the table only shows sections that have keys.
 */
function buildSections(rows: IRow[]): IGroupSection[] {
  const byGroup = new Map<TSettingsKeyGroup, IRow[]>()
  for (const row of rows) {
    const existing = byGroup.get(row.group)
    if (existing) existing.push(row)
    else byGroup.set(row.group, [row])
  }
  const sections: IGroupSection[] = []
  for (const meta of ADMIN_SETTINGS_GROUP_META) {
    const groupRows = byGroup.get(meta.value)
    if (!groupRows || groupRows.length === 0) continue
    groupRows.sort((a, b) => {
      const aOverride = a.state === "override" ? 0 : 1
      const bOverride = b.state === "override" ? 0 : 1
      if (aOverride !== bOverride) return aOverride - bOverride
      return a.label.localeCompare(b.label)
    })
    sections.push({
      group: meta.value,
      rows: groupRows,
      overridden: groupRows.filter((row) => row.state === "override").length,
    })
  }
  return sections
}

function maskValue(value: string): string {
  if (value.length === 0) return ""
  if (value.length <= 8) return "•".repeat(value.length)
  return `${value.slice(0, 4)}${"•".repeat(Math.min(20, value.length - 8))}${value.slice(-4)}`
}

interface IEditModalProps {
  companyId: string
  initialKey: string
  initialValue: string
  onClose: () => void
}

function engineRoleForKey(key: string): TEnsembleRole | null {
  if (key === "ensemble_word_engines" || key === "default_ensemble_word_engines") return "words"
  if (key === "ensemble_turn_engines" || key === "default_ensemble_turn_engines") return "turns"
  return null
}

function parseOverrideEngineList(role: TEnsembleRole, raw: string): string[] {
  const parsed = validateEngineList(role, raw)
  if (parsed.ok) return [...parsed.engines]
  return []
}

function EditOverrideModal({ companyId, initialKey, initialValue, onClose }: IEditModalProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const { i18n } = useLingui()
  const [value, setValue] = useState(initialValue)
  const [saveError, setSaveError] = useState<string | null>(null)
  const isSecret = isSecretKey(initialKey)
  const engineRole = engineRoleForKey(initialKey)
  const isModeKey = initialKey === "transcription_mode" || initialKey === "default_transcription_mode"
  const isMergeKey = initialKey === "ensemble_merge_mode" || initialKey === "default_ensemble_merge_mode"
  const isHeadcountKey = initialKey === "ensemble_headcount_tie" || initialKey === "default_ensemble_headcount_tie"
  const isAdjudicateKey =
    initialKey === "ensemble_adjudicate_speakers" || initialKey === "default_ensemble_adjudicate_speakers"

  const { mutate, isPending } = useMutation({
    mutationFn: () => updateCompanySettings(companyId, { [initialKey]: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.companySettings(companyId) })
      notifications.show({
        title: i18n._(msg`Saved`),
        message: i18n._(msg`Override set for ${initialKey}`),
        color: "success",
      })
      onClose()
    },
    onError: (err) => {
      setSaveError(extractErrorMessage(err))
      notifyError(err)
    },
  })

  const turnList = engineRole === "turns" ? parseOverrideEngineList("turns", value) : []

  return (
    <Modal opened onClose={onClose} title={i18n._(msg`Override ${labelForKey(initialKey)}`)} centered>
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          <Trans>This value is stored at the org scope and overrides the platform default for this company only.</Trans>
        </Text>
        {isSecret ? (
          <PasswordInput
            label={i18n._(msg`Value`)}
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            autoFocus
          />
        ) : isModeKey ? (
          <Select
            label={i18n._(msg`Finished transcript: engines`)}
            data={[
              { value: "two-pass", label: i18n._(msg`Two-pass`) },
              { value: "ensemble", label: i18n._(msg`Ensemble`) },
            ]}
            value={isTranscriptionMode(value) ? value : "ensemble"}
            onChange={(next) => next && isTranscriptionMode(next) && setValue(next)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
        ) : isMergeKey ? (
          <Select
            label={i18n._(msg`Merge behaviour`)}
            data={[
              { value: "constrained", label: i18n._(msg`Constrained — only choose between what the engines heard`) },
              { value: "rewrite", label: i18n._(msg`Rewrite — let the AI reword disputed passages`) },
            ]}
            value={isEnsembleMergeMode(value) ? value : "constrained"}
            onChange={(next) => next && isEnsembleMergeMode(next) && setValue(next)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
        ) : isHeadcountKey ? (
          <Select
            label={i18n._(msg`Headcount tie`)}
            data={[
              { value: "lower", label: i18n._(msg`Lower — fewer speakers`) },
              { value: "higher", label: i18n._(msg`Higher — more speakers`) },
            ]}
            value={isEnsembleHeadcountTie(value) ? value : "lower"}
            onChange={(next) => next && isEnsembleHeadcountTie(next) && setValue(next)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
        ) : isAdjudicateKey ? (
          <Switch
            label={i18n._(msg`AI decides disputed speakers`)}
            checked={value === "true"}
            onChange={(e) => setValue(e.currentTarget.checked ? "true" : "false")}
          />
        ) : engineRole ? (
          <div>
            <MultiSelect
              label={labelForKey(initialKey)}
              data={(engineRole === "words" ? WORDS_CAPABLE_PROVIDERS : TURNS_CAPABLE_PROVIDERS).map((id) => ({
                value: id,
                label: TRANSCRIPTION_LABELS[id],
              }))}
              value={parseOverrideEngineList(engineRole, value)}
              onChange={(vals) => setValue(JSON.stringify(vals))}
              hidePickedOptions
              comboboxProps={{ withinPortal: true }}
            />
            {engineRole === "turns" && turnList.length === 2 && (
              <Alert variant="light" color="warning" mt="xs">
                <Text size="sm">
                  <Trans>
                    Two turns engines cannot break a headcount tie on their own; the headcount-tie setting decides.
                  </Trans>
                </Text>
              </Alert>
            )}
          </div>
        ) : (
          <TextInput
            label={i18n._(msg`Value`)}
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            autoFocus
          />
        )}
        {saveError && (
          <Alert variant="light" color="danger">
            <Text size="sm">{saveError}</Text>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose} disabled={isPending}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={() => mutate()} loading={isPending}>
            <Trans>Save override</Trans>
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

interface IResetModalProps {
  companyId: string
  resetKey: string
  platformValue: string | undefined
  onClose: () => void
}

function ResetOverrideModal({ companyId, resetKey, platformValue, onClose }: IResetModalProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteCompanySettingOverride(companyId, resetKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.companySettings(companyId) })
      notifications.show({
        title: i18n._(msg`Override removed`),
        message: i18n._(msg`${resetKey} now uses the platform default`),
        color: "success",
      })
      onClose()
    },
    onError: (err) => notifyError(err),
  })

  const isSecret = isSecretKey(resetKey)
  const display = platformValue
    ? isSecret
      ? maskValue(platformValue)
      : platformValue
    : i18n._(msg`(no platform default — setting will be empty)`)

  return (
    <Modal opened onClose={onClose} title={i18n._(msg`Reset ${labelForKey(resetKey)}`)} centered>
      <Stack gap="md">
        <Text size="sm">
          <Trans>Remove the org override and fall back to the platform default:</Trans>
        </Text>
        <Text size="sm" ff="monospace" c="dimmed">
          {display}
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose} disabled={isPending}>
            <Trans>Cancel</Trans>
          </Button>
          <Button color="danger" onClick={() => mutate()} loading={isPending}>
            <Trans>Remove override</Trans>
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function CompanySettingsPage(): React.JSX.Element {
  const { id } = Route.useParams()
  const { i18n } = useLingui()
  const [search, setSearch] = useState("")
  const [revealKey, setRevealKey] = useState<Record<string, boolean>>({})
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null)
  const [resetting, setResetting] = useState<{ key: string; platformValue: string | undefined } | null>(null)
  // The 4-column override table overflows on phones — swap to a card list below
  // the small breakpoint. Cell content is shared via the render helpers below.
  const isMobile = useMediaQuery("(max-width: 48em)", false)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.admin.companySettings(id),
    queryFn: () => fetchCompanySettings(id),
  })

  // Translated group label — memoized so it can also feed the search filter below.
  const groupLabel = useCallback(
    (group: TSettingsKeyGroup): string => {
      const meta = ADMIN_SETTINGS_GROUP_META.find((entry) => entry.value === group)
      return meta ? i18n._(meta.label) : group
    },
    [i18n],
  )

  // Search matches across raw key, human label, and group label; then bucket into
  // grouped sections (overrides first). Empty search shows every configured key.
  const sections = useMemo(() => {
    if (!data) return []
    const all = buildRows(data.platform, data.org)
    const query = search.trim().toLowerCase()
    const matched = query
      ? all.filter(
          (row) =>
            row.key.toLowerCase().includes(query) ||
            row.label.toLowerCase().includes(query) ||
            groupLabel(row.group).toLowerCase().includes(query),
        )
      : all
    return buildSections(matched)
  }, [data, search, groupLabel])

  const hasRows = sections.length > 0

  // ── Shared cell content (used by both the desktop table and mobile cards) ──
  const renderSource = (state: TRowState): React.JSX.Element | null => {
    if (state === "override")
      return (
        <Tooltip label={i18n._(msg`This company has a custom value`)}>
          <Badge color="info" variant="light">
            <Trans>Custom override</Trans>
          </Badge>
        </Tooltip>
      )
    if (state === "default")
      return (
        <Tooltip label={i18n._(msg`Inherits from platform — no company-specific value`)}>
          <Badge color="gray" variant="light">
            <Trans>Platform default</Trans>
          </Badge>
        </Tooltip>
      )
    return (
      <Tooltip label={i18n._(msg`Neither platform nor org has a value`)}>
        <Badge color="danger" variant="light">
          <Trans>Not configured</Trans>
        </Badge>
      </Tooltip>
    )
  }

  const renderValue = (row: IRow): React.JSX.Element => {
    const secret = isSecretKey(row.key)
    const revealed = revealKey[row.key] === true
    const displayValue = !row.effectiveValue
      ? "—"
      : secret && !revealed
        ? maskValue(row.effectiveValue)
        : row.effectiveValue
    return (
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" ff="monospace" style={{ wordBreak: "break-all" }}>
          {displayValue}
        </Text>
        {secret && row.effectiveValue && (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => setRevealKey((s) => ({ ...s, [row.key]: !revealed }))}
            title={revealed ? i18n._(msg`Hide`) : i18n._(msg`Show`)}
            aria-label={revealed ? i18n._(msg`Hide value`) : i18n._(msg`Show value`)}
          >
            {revealed ? <IconEyeOff size={16} stroke={1.6} /> : <IconEye size={16} stroke={1.6} />}
          </ActionIcon>
        )}
      </Group>
    )
  }

  const renderActions = (row: IRow): React.JSX.Element => (
    <Group gap="xs" wrap="nowrap">
      <Button size="xs" variant="light" onClick={() => setEditing({ key: row.key, value: row.orgValue ?? "" })}>
        {row.state === "override" ? <Trans>Edit</Trans> : <Trans>Override</Trans>}
      </Button>
      {row.state === "override" && (
        <Button
          size="xs"
          variant="subtle"
          color="danger"
          onClick={() => setResetting({ key: row.key, platformValue: row.platformValue })}
        >
          <Trans>Reset</Trans>
        </Button>
      )}
    </Group>
  )

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="lg">
        <Group>
          <Anchor renderRoot={(props) => <Link to="/companies/$id" params={{ id }} {...props} />} size="sm">
            &larr; <Trans>Company</Trans>
          </Anchor>
          <Title order={2}>
            <Trans>Company settings</Trans>
          </Title>
        </Group>
      </Group>

      <Stack gap="lg">
        <TextInput
          leftSection={<IconSearch size={16} stroke={1.6} />}
          placeholder={i18n._(msg`Search settings by name, key, or group`)}
          aria-label={i18n._(msg`Search settings`)}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {isLoading && <TableSkeleton rows={6} cols={4} />}

        {!isLoading && !hasRows && (
          <Text c="dimmed" ta="center" py="xl">
            {search
              ? i18n._(msg`No settings match your search.`)
              : i18n._(msg`No platform or org settings configured yet.`)}
          </Text>
        )}

        {!isLoading &&
          hasRows &&
          sections.map((section) => {
            const meta = ADMIN_SETTINGS_GROUP_META.find((entry) => entry.value === section.group)
            const GroupIcon = meta?.Icon
            const keyCount = section.rows.length
            const overriddenCount = section.overridden
            return (
              <Stack key={section.group} gap="xs">
                <Box
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "var(--mantine-color-body)",
                    paddingBlock: "var(--mantine-spacing-xs)",
                  }}
                >
                  <Group gap="xs" wrap="nowrap">
                    {GroupIcon && <GroupIcon size={18} stroke={1.6} aria-hidden />}
                    <Text fw={600}>{meta ? i18n._(meta.label) : section.group}</Text>
                    <Text size="xs" c="dimmed">
                      <Plural value={keyCount} one="# key" other="# keys" />
                      {" · "}
                      <Trans>{overriddenCount} overridden</Trans>
                    </Text>
                  </Group>
                </Box>

                {isMobile ? (
                  <Stack gap="sm">
                    {section.rows.map((row) => (
                      <Paper key={row.key} withBorder p="sm" radius="md">
                        {/* Badge on its own row so a long key can't crush it. */}
                        <Group gap="xs" mb={6}>
                          {renderSource(row.state)}
                        </Group>
                        <Text size="sm" fw={600} mb={2} style={{ wordBreak: "break-word" }}>
                          {row.label}
                        </Text>
                        <Text ff="monospace" size="xs" c="dimmed" mb="xs" style={{ wordBreak: "break-all" }}>
                          {row.key}
                        </Text>
                        <div style={{ marginBottom: "var(--mantine-spacing-sm)" }}>{renderValue(row)}</div>
                        {renderActions(row)}
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Table striped withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>
                          <Trans>Key</Trans>
                        </Table.Th>
                        <Table.Th style={{ width: 160 }}>
                          <Trans>Source</Trans>
                        </Table.Th>
                        <Table.Th>
                          <Trans>Value</Trans>
                        </Table.Th>
                        <Table.Th style={{ width: 220 }}>
                          <Trans>Actions</Trans>
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {section.rows.map((row) => (
                        <Table.Tr key={row.key}>
                          <Table.Td>
                            <Text size="sm" fw={600} style={{ wordBreak: "break-word" }}>
                              {row.label}
                            </Text>
                            <Text ff="monospace" size="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
                              {row.key}
                            </Text>
                          </Table.Td>
                          <Table.Td>{renderSource(row.state)}</Table.Td>
                          <Table.Td>{renderValue(row)}</Table.Td>
                          <Table.Td>{renderActions(row)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            )
          })}
      </Stack>

      {editing && (
        <EditOverrideModal
          companyId={id}
          initialKey={editing.key}
          initialValue={editing.value}
          onClose={() => setEditing(null)}
        />
      )}
      {resetting && (
        <ResetOverrideModal
          companyId={id}
          resetKey={resetting.key}
          platformValue={resetting.platformValue}
          onClose={() => setResetting(null)}
        />
      )}
    </Container>
  )
}

export const Route = createFileRoute("/_auth/companies/$id_/settings")({
  beforeLoad: requireAdmin,
  component: CompanySettingsPage,
})
